import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { animateSlotAtcAddedCycle } from '@theme/product-form';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize?: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} stickyAddToCartButton - Sticky bar's button
 * @property {HTMLImageElement} [productImage] - Product image for fly-to-cart
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'stickyAddToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #didPuppetClick = false;

  /** @type {{ clear: () => void }} */
  #slotAtcCycle = { clear: () => {} };

  connectedCallback() {
    super.connectedCallback();

    this.#ensureIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectObservers();
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#slotAtcCycle.clear();
  }

  /**
   * Retries observer setup until buy buttons exist in the DOM.
   */
  #ensureIntersectionObserver() {
    if (this.#buyButtonsIntersectionObserver) return;

    if (this.#setupIntersectionObserver()) return;

    requestAnimationFrame(() => this.#ensureIntersectionObserver());
  }

  #disconnectObservers() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;
    this.#mainBottomObserver = null;
  }

  /**
   * Sets up IntersectionObservers for buy buttons and footer.
   * @returns {boolean} Whether setup succeeded
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return false;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return false;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return false;

    this.#disconnectObservers();

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    return true;
  }

  /**
   * Handles the sticky bar add to cart click — delegates to the main form button.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) {
      this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    }
    if (!this.#targetAddToCartButton) return;

    this.#didPuppetClick = true;
    this.#targetAddToCartButton.dataset.puppet = 'true';

    const btn = this.refs.stickyAddToCartButton;
    const flyToCartEnabled = this.getAttribute('data-add-to-cart-animation') === 'true';

    if (btn && !btn.disabled) {
      this.#slotAtcCycle.clear();
      this.#slotAtcCycle = animateSlotAtcAddedCycle(btn);
    }

    this.#targetAddToCartButton.click();

    if (btn?.dataset.added !== 'true') {
      btn.dataset.added = 'true';
    }

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !btn) return;

    if (!flyToCartEnabled || !this.refs.productImage) {
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
      this.#resetTimeout = setTimeout(() => {
        btn.removeAttribute('data-added');
      }, 800);
      return;
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([btn, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      btn.removeAttribute('data-added');
    }, 800);
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (String(event.detail.data.productId) !== String(this.dataset.productId)) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.#isStuck = currentStuck === 'true';
    this.dataset.variantAvailable = variantAvailable;

    if (variant?.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    if (this.dataset.variantAvailable !== 'true' && this.#isStuck) {
      this.#hideStickyBar();
    }

    this.#ensureIntersectionObserver();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Resets puppet flag and sticky slot label after cart operations.
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    this.#didPuppetClick = false;
  };

  #showStickyBar() {
    if (this.dataset.variantAvailable !== 'true') return;

    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';

    // Publish the bar's real height so the docked controls above it clear it
    // exactly. The CSS carries a sensible default; this makes it precise for a
    // long product title or a translated label.
    const height = this.refs.stickyBar.offsetHeight;
    if (height) {
      document.documentElement.style.setProperty('--docked-bar-height', `${height}px`);
    }
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
